/* eslint-disable @typescript-eslint/no-explicit-any */
import { findIndex } from 'lodash';
import {
  KeyboardEvent,
  MouseEvent,
  RefObject,
  useCallback,
  useRef,
  useState,
} from 'react';

export enum ContextMenuHorizontalPosition {
  Default = 'DEFAULT',
  Left = 'LEFT',
  Right = 'RIGHT',
}

export enum ContextMenuVerticalPosition {
  Default = 'DEFAULT',
  Top = 'TOP',
  Bottom = 'BOTTOM',
}

export type ContextMenuState = {
  ref: RefObject<HTMLDivElement>;
  data: {
    [key: string]: any;
    center: boolean;
    horizontal: ContextMenuHorizontalPosition;
    vertical: ContextMenuVerticalPosition;
  };
  x: number;
  y: number;
  width: number;
  height: number;
};

export function useContextMenu(
  variables: {
    center?: boolean;
    horizontal?: ContextMenuHorizontalPosition;
    vertical?: ContextMenuVerticalPosition;
  } | null = null
) {
  const contextMenuRef = useRef<HTMLDivElement>(null);

  const [contextMenuState, setContextMenuState] = useState<
    ContextMenuState | false
  >(false);

  const onCloseContextMenu = useCallback((e?: UIEvent) => {
    if (e) {
      const currentRef = e.composedPath().find(option => {
        const { classList } = option as HTMLElement;

        return !!(
          classList?.contains('context-modal') ||
          classList?.contains('modal-container')
        );
      });

      const refIndex = findIndex(
        document.getElementById('portal-container')?.children,
        option => option === contextMenuRef.current
      );

      const modalIndex = findIndex(
        document.getElementById('portal-container')?.children,
        option => option === currentRef
      );

      if (refIndex > modalIndex) {
        setTimeout(() => {
          setContextMenuState(false);
        });

        window.removeEventListener('resize', onCloseContextMenu, true);
        document.removeEventListener('click', onCloseContextMenu, true);
        document.removeEventListener('wheel', onCloseContextMenu, true);
        document.removeEventListener('touchmove', onCloseContextMenu, true);
      }
    } else {
      setTimeout(() => {
        setContextMenuState(false);
      });

      window.removeEventListener('resize', onCloseContextMenu, true);
      document.removeEventListener('click', onCloseContextMenu, true);
      document.removeEventListener('wheel', onCloseContextMenu, true);
      document.removeEventListener('touchmove', onCloseContextMenu, true);
    }
  }, []);

  const onOpenContextMenu = useCallback(
    (
      e: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>,
      data: { [key: string]: any } | null = null
    ) => {
      e.stopPropagation();
      e.preventDefault();

      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();

      setTimeout(() => {
        setContextMenuState({
          ref: contextMenuRef,
          data: {
            ...(data ?? {}),
            center: variables?.center ?? true,
            horizontal:
              variables?.horizontal ?? ContextMenuHorizontalPosition.Default,
            vertical:
              variables?.vertical ?? ContextMenuVerticalPosition.Default,
          },
          x: (rect.left + rect.right) * 0.5,
          y: (rect.top + rect.bottom) * 0.5,
          width: rect.width,
          height: rect.height,
        });
      });

      window.addEventListener('resize', onCloseContextMenu, true);
      document.addEventListener('click', onCloseContextMenu, true);
      document.addEventListener('wheel', onCloseContextMenu, true);
      document.addEventListener('touchmove', onCloseContextMenu, true);
    },
    [onCloseContextMenu, variables]
  );

  return {
    contextMenuRef,
    contextMenuState,
    onOpenContextMenu,
    onCloseContextMenu,
  };
}
